import { ThemeProvider } from 'styled-components';
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import './App.css';
import Routes from './containers';
import { NotificationWebSocketBridge } from 'components/NotificationWebSocketBridge';

import { UserContext } from './context/context';
import { Sidebar } from 'components/Sidebar';
import { MobileSidebarDrawer } from 'components/MobileSidebarDrawer';
import { useSelector } from 'react-redux';
import { AUTH_SESSION_CHANGED_EVENT, readAuthSession } from 'utils/authSession';
import { useLocation } from 'react-router-dom';
import { setSentryRouteContext, setSentryUserFromAuthSession } from 'utils/sentry';
import {
  desktopSidebarMediaQuery,
  SIDEBAR_DESKTOP_MIN_WIDTH,
  SIDEBAR_MOBILE_MAX_WIDTH,
} from 'constants/layoutBreakpoints';

function App() {
  const [menuOpenInMobile, setMenuOpenInMobile] = useState(false);
  const [showSidebar, setShowSidebar] = useState(() => readAuthSession().isSessionComplete);
  const location = useLocation();

  const styledComponentTheme = {
    sm: '540px', // small tablet
    md: '720px', // tablet
    lg: '960px', // desktop
    xl: '1140px', // large desktop
    '2xl': SIDEBAR_DESKTOP_MIN_WIDTH,
    mobileSidebarMax: SIDEBAR_MOBILE_MAX_WIDTH,
  };

  const collapsed = useSelector((state) => state.getIn(['ui', 'collapsed']));

  const closeMobileMenu = useCallback(() => {
    setMenuOpenInMobile(false);
  }, []);

  const toggleMobileMenu = useCallback(() => {
    setMenuOpenInMobile((open) => !open);
  }, []);

  const userContextValue = useMemo(
    () => ({
      menuOpenInMobile,
      setMenuOpenInMobile,
      closeMobileMenu,
      toggleMobileMenu,
    }),
    [menuOpenInMobile, closeMobileMenu, toggleMobileMenu],
  );

  // Re-sync sidebar when auth is written (same tab) or cleared (other tabs)
  useEffect(() => {
    const syncSidebar = () => {
      const session = readAuthSession();
      const next = session.isSessionComplete;
      setShowSidebar((prev) => (prev === next ? prev : next));
      setSentryUserFromAuthSession();
    };

    syncSidebar();
    window.addEventListener(AUTH_SESSION_CHANGED_EVENT, syncSidebar);
    window.addEventListener('storage', syncSidebar);
    return () => {
      window.removeEventListener(AUTH_SESSION_CHANGED_EVENT, syncSidebar);
      window.removeEventListener('storage', syncSidebar);
    };
  }, []);

  useEffect(() => {
    setSentryRouteContext(location.pathname, location.search);
  }, [location.pathname, location.search]);

  // Close mobile drawer after navigation.
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, location.search, closeMobileMenu]);

  // Close mobile drawer when returning to desktop width.
  useEffect(() => {
    const mediaQuery = window.matchMedia(desktopSidebarMediaQuery);
    const handleViewportChange = (event) => {
      if (event.matches) {
        closeMobileMenu();
      }
    };

    if (mediaQuery.matches) {
      closeMobileMenu();
    }

    mediaQuery.addEventListener('change', handleViewportChange);
    return () => mediaQuery.removeEventListener('change', handleViewportChange);
  }, [closeMobileMenu]);

  return (
    <Container className="App">
      <UserContext.Provider value={userContextValue}>
        <ThemeProvider theme={styledComponentTheme}>
          <NotificationWebSocketBridge />
          {showSidebar && (
            <React.Fragment>
              <Sidebar isMobile={true} collapsed={collapsed} />
              {menuOpenInMobile && (
                <MobileSidebarDrawer onClose={closeMobileMenu}>
                  <Sidebar collapsed={false} isMobile={false} hideCollapseToggle />
                </MobileSidebarDrawer>
              )}
            </React.Fragment>
          )}
          <BobyContainer>
            <Routes />
          </BobyContainer>
        </ThemeProvider>
      </UserContext.Provider>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
`;

const BobyContainer = styled.div`
  flex: 1;
  min-height: 100%;
  overflow: hidden;
`;

export default App;
